import React from 'react';
import { Seo } from '@/components/common/Seo';
import { FadeIn } from '@/components/common/FadeIn';
import { SectionLabel } from '@/components/common/SectionLabel';
import { PageHero } from '@/components/common/PageHero';
import { OphirButton } from '@/components/common/OphirButton';
import { Icon } from '@/components/common/Icon';
import { ProcessDiagram } from '@/components/graphics/ProcessDiagram';
import { useContent } from '@/context/ContentContext';

export default function Capabilities() {
  const { content } = useContent();
  const seo = content?.seo?.capabilities;
  const capabilities = content?.capabilities || [];
  const process = content?.process || [];
  return (
    <>
      <Seo title={seo?.title} description={seo?.description} />
      <PageHero
        label="Capabilities"
        title="What we do"
        intro="From wholesale purchasing to global product sourcing, supplier partnerships, commercial distribution and market opportunity evaluation — our capabilities are built around dependable, well-documented supply relationships."
        image="/images/ophir/capabilities/sourcing.webp"
        imageAlt="A procurement professional reviewing neutral consumer-packaged-goods samples and packaging specifications"
      />

      <section className="bg-white">
        <div className="container-ophir py-16 lg:py-24">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {capabilities.map((c, i) => (
              <FadeIn key={i} delay={(i % 2) * 0.06}>
                <div className="h-full bg-ophir-ivory-soft border border-ophir-grey rounded-2xl p-8 shadow-ophir">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-ophir-navy/5 text-ophir-navy">
                    <Icon name={c.icon} size={22} />
                  </span>
                  <h2 className="mt-5 font-serif text-[22px] text-ophir-navy-deep">{c.title}</h2>
                  <p className="mt-3 text-[15.5px] leading-relaxed text-ophir-muted">{c.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ophir-ivory-soft border-t border-ophir-grey">
        <div className="container-ophir py-16 lg:py-24">
          <FadeIn className="max-w-2xl mb-12">
            <SectionLabel>Our process</SectionLabel>
            <h2 className="mt-5 font-serif text-[30px] sm:text-[38px] leading-tight text-ophir-navy-deep">How an opportunity progresses</h2>
            <p className="mt-5 text-[16.5px] leading-relaxed text-ophir-muted">A clear, six-step path from first introduction to an ongoing, well-managed relationship.</p>
          </FadeIn>
          <FadeIn><ProcessDiagram steps={process.length ? process : undefined} /></FadeIn>
        </div>
      </section>

      <section className="navy-gradient">
        <div className="container-ophir py-16 text-center">
          <FadeIn>
            <h2 className="font-serif text-[28px] sm:text-[36px] text-white max-w-2xl mx-auto leading-tight">Discuss a supply opportunity with our team</h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <OphirButton variant="gold" to="/suppliers">Become a Supplier</OphirButton>
              <OphirButton variant="secondary" to="/buyers" className="!bg-transparent !border-white/50 !text-white hover:!bg-white/10">Make a Buyer Inquiry</OphirButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </>
  );
}
